import React, { useEffect, useState } from 'react';
import { Calendar, CheckCircle, Clock, Target } from 'lucide-react';

interface TimelineProgressBarProps {
  totalTasks: number;
  completedTasks: number;
  daysUntilWedding: number;
  weddingDate: string;
}

const TimelineProgressBar: React.FC<TimelineProgressBarProps> = ({
  totalTasks,
  completedTasks,
  daysUntilWedding,
  weddingDate
}) => {
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const progressPercentage = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  // 動畫效果：進度條增長
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedProgress(progressPercentage);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [progressPercentage]);

  const formatWeddingDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  };

  const getProgressColor = () => {
    if (progressPercentage >= 80) return 'from-green-500 to-green-600';
    if (progressPercentage >= 60) return 'from-wedding-gold to-yellow-500';
    if (progressPercentage >= 40) return 'from-wedding-pink to-pink-500';
    return 'from-gray-400 to-gray-500';
  };

  const getMotivationalMessage = () => {
    if (progressPercentage >= 90) return '快要完成了！🎉';
    if (progressPercentage >= 70) return '進展很順利！💪';
    if (progressPercentage >= 50) return '已經完成一半了！👍';
    if (progressPercentage >= 25) return '好的開始！✨';
    return '開始規劃您的完美婚禮！💕';
  };

  return (
    <div className="bg-gradient-to-r from-wedding-pink/10 to-wedding-gold/10 rounded-lg p-6 mb-6 border border-wedding-pink/20">
      {/* 標題區域 */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-wedding-text-title flex items-center gap-2">
            <Target className="w-5 h-5 text-wedding-pink" />
            籌備進度
          </h3>
          <p className="text-sm text-wedding-text-body mt-1">
            {getMotivationalMessage()}
          </p>
        </div>
        
        <div className="text-right">
          <div className="flex items-center gap-2 text-wedding-text-body mb-1">
            <Calendar className="w-4 h-4" />
            <span className="text-sm">
              {daysUntilWedding > 0 ? `還有 ${daysUntilWedding} 天` : '婚禮已過'}
            </span>
          </div>
          <div className="text-xs text-wedding-text-body">
            {formatWeddingDate(weddingDate)}
          </div>
        </div>
      </div>
      
      {/* 進度條容器 */}
      <div className="mb-4">
        <div className="w-full bg-gray-200 rounded-full h-4 relative overflow-hidden">
          {/* 背景動畫條紋 */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse opacity-20"></div>
          
          {/* 主進度條 */}
          <div 
            className={`bg-gradient-to-r ${getProgressColor()} h-4 rounded-full transition-all duration-1000 ease-out relative`}
            style={{ width: `${animatedProgress}%` }}
          >
            {/* 進度條光澤效果 */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
          </div>
        </div>
      </div>
      
      {/* 統計資訊 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-wedding-text-title mb-1">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-2xl font-bold">{completedTasks}</span>
          </div>
          <div className="text-xs text-wedding-text-body">已完成</div>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-wedding-text-title mb-1">
            <Clock className="w-4 h-4 text-wedding-pink" />
            <span className="text-2xl font-bold">{totalTasks - completedTasks}</span>
          </div>
          <div className="text-xs text-wedding-text-body">待完成</div>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-wedding-text-title mb-1">
            <Target className="w-4 h-4 text-wedding-gold" />
            <span className="text-2xl font-bold">{totalTasks}</span>
          </div>
          <div className="text-xs text-wedding-text-body">總任務</div>
        </div>
        
        <div className="text-center">
          <div className="text-2xl font-bold text-wedding-text-title mb-1">
            {Math.round(progressPercentage)}%
          </div>
          <div className="text-xs text-wedding-text-body">完成度</div>
        </div>
      </div>
      
      {/* 底部詳細資訊 */}
      <div className="mt-4 pt-4 border-t border-wedding-pink/20">
        <div className="flex justify-between text-sm text-wedding-text-body">
          <span>目前進度：{completedTasks} / {totalTasks} 項任務</span>
          <span>
            {daysUntilWedding > 0 && totalTasks > completedTasks && (
              `平均每天需完成 ${Math.ceil((totalTasks - completedTasks) / daysUntilWedding)} 項任務`
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TimelineProgressBar;